import { useState, useEffect } from 'react'
import { useCreditLimits, useUpdateCreditLimits } from '@/hooks/useCredits'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Save, Loader2, AlertCircle } from 'lucide-react'

export const CreditLimitsConfig = () => {
  const { data: limits, isLoading } = useCreditLimits()
  const updateLimits = useUpdateCreditLimits()

  const [minPerSearch, setMinPerSearch] = useState<number>(0)
  const [maxPerSearch, setMaxPerSearch] = useState<number>(0)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (limits) {
      setMinPerSearch(limits.min_per_search)
      setMaxPerSearch(limits.max_per_search)
    }
  }, [limits])

  const hasChanges =
    limits && (minPerSearch !== limits.min_per_search || maxPerSearch !== limits.max_per_search)

  const handleSave = async () => {
    setError('')

    if (minPerSearch >= maxPerSearch) {
      setError('El mínimo debe ser menor que el máximo')
      return
    }

    if (minPerSearch < 1) {
      setError('El mínimo debe ser al menos 1')
      return
    }

    if (maxPerSearch < 100) {
      setError('El máximo debe ser al menos 100')
      return
    }

    await updateLimits.mutateAsync({
      min_per_search: minPerSearch,
      max_per_search: maxPerSearch,
    })
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Límites de Créditos por Búsqueda</CardTitle>
        <CardDescription>
          Configura los límites globales para la cantidad de créditos que pueden asignarse a una
          búsqueda personalizada
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="min-credits">Mínimo de créditos</Label>
            <Input
              id="min-credits"
              type="number"
              min={1}
              value={minPerSearch}
              onChange={(e) => setMinPerSearch(Number(e.target.value))}
              disabled={updateLimits.isPending}
            />
            <p className="text-sm text-muted-foreground">
              Cantidad mínima de créditos que puede asignar un usuario
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-credits">Máximo de créditos</Label>
            <Input
              id="max-credits"
              type="number"
              min={100}
              value={maxPerSearch}
              onChange={(e) => setMaxPerSearch(Number(e.target.value))}
              disabled={updateLimits.isPending}
            />
            <p className="text-sm text-muted-foreground">
              Cantidad máxima de créditos que puede asignar un usuario
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!hasChanges || updateLimits.isPending}>
            {updateLimits.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Guardar cambios
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
